/**
 * Settings → Customers (multi-customer, Phase 3).
 *
 * Left: list of customers grouped by type, with "+ Add". Right (modal): the
 * editor — basic info, module toggles, departments CRUD, and the pricing /
 * services editor the boss asked for (per-customer charge AND sub-pay, plus
 * "create new service" and "copy pricing from another customer").
 *
 * All writes go through api.* → /api/customers; the list refetches after each
 * save so the workspace switcher and the rest of the app stay in sync.
 */

import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { useCustomer } from '../contexts/CustomerContext';

const NAVY = '#1e3a4a', NAVY_LT = '#4A7A91', LINE = '#e5e7eb', MUTED = '#6b7280';

const TYPE_LABEL = {
  dealership_full:    'Dealership — Full',
  dealership_partial: 'Dealership — Partial',
  private_pool:       'Private — Walk-in pool',
  private_recurring:  'Private — Recurring B2B',
};
const TYPE_OPTIONS = Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label }));

const MODULES = [
  ['has_daily_invoicing',  'Daily Used Cars invoicing'],
  ['has_weekly_invoicing', 'Weekly department invoicing'],
  ['has_office_cleaning',  'Office cleaning'],
  ['has_service_wash',     'Service Wash Daily'],
  ['has_detailing',        'Detailing services'],
  ['has_buffing',          'Buffing authorization'],
];

export default function CustomersTab() {
  const { refetch: refetchContext } = useCustomer();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editingId, setEditingId] = useState(null); // number | 'new' | null

  async function load() {
    setLoading(true);
    try { setCustomers(await api.listCustomers()); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  function closeEditor(changed) {
    setEditingId(null);
    if (changed) { load(); refetchContext(); }
  }

  const groups = [
    { label: '🏢 Dealerships', types: ['dealership_full', 'dealership_partial'] },
    { label: '👤 Private Customers', types: ['private_pool', 'private_recurring'] },
  ];

  return (
    <div style={{ padding: 20, maxWidth: 1080, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: NAVY }}>Customers</h2>
        <button onClick={() => setEditingId('new')} style={btnPrimary}>+ Add Customer</button>
      </div>

      {loading ? (
        <div style={{ color: MUTED, fontSize: 13, padding: 30, textAlign: 'center' }}>Loading…</div>
      ) : (
        groups.map(g => {
          const items = customers.filter(c => g.types.includes(c.type));
          if (items.length === 0) return null;
          return (
            <div key={g.label} style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: NAVY_LT, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 10 }}>
                {g.label}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                {items.map(c => (
                  <CustomerCard key={c.id} c={c} onEdit={() => setEditingId(c.id)} />
                ))}
              </div>
            </div>
          );
        })
      )}

      {editingId && (
        <CustomerEditor id={editingId} onClose={closeEditor} allCustomers={customers} />
      )}
    </div>
  );
}

function CustomerCard({ c, onEdit }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 12, padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.color_hex || NAVY }} />
        <strong style={{ fontSize: 14, color: NAVY }}>{c.name}</strong>
        {c.is_default ? <span style={tagStyle('#e0f2fe', '#0369a1')}>main</span> : null}
        {c.status !== 'active' ? <span style={tagStyle('#fee2e2', '#991b1b')}>{c.status}</span> : null}
      </div>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 10 }}>
        {TYPE_LABEL[c.type]} · code {c.short_code}
      </div>
      <button onClick={onEdit} style={btnGhost}>Edit</button>
    </div>
  );
}

// ── Editor modal ────────────────────────────────────────────────────────────
function CustomerEditor({ id, onClose, allCustomers }) {
  const isNew = id === 'new';
  const [form, setForm]       = useState(blankForm());
  const [depts, setDepts]     = useState([]);
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [err, setErr]         = useState('');
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const c = await api.getCustomer(id);
        setForm({ ...blankForm(), ...c });
        setDepts(c.departments || []);
        setPricing(c.pricing || []);
      } catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, [id, isNew]);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function saveBasics() {
    setErr('');
    if (!form.name?.trim())       return setErr('Name is required');
    if (!form.short_code?.trim()) return setErr('Short code is required');
    if (!form.type)               return setErr('Type is required');
    setSaving(true);
    try {
      if (isNew) {
        const created = await api.createCustomer(form);
        // Switch the editor to the freshly-created id so depts/pricing attach.
        setChanged(true);
        onClose(true);
        return created;
      }
      await api.updateCustomer(id, form);
      setChanged(true);
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  if (loading) {
    return <Modal onClose={() => onClose(changed)}><div style={{ padding: 30, color: MUTED }}>Loading…</div></Modal>;
  }

  return (
    <Modal onClose={() => onClose(changed)} wide>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: NAVY }}>
          {isNew ? 'New Customer' : `Edit — ${form.name}`}
        </h3>
        <button onClick={() => onClose(changed)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: MUTED }}>✕</button>
      </div>

      {err && <div style={errBox}>{err}</div>}

      {/* Section 1 — Basic info */}
      <Section title="Basic info">
        <Grid>
          <Field label="Name *"><input style={inp} value={form.name || ''} onChange={e => set('name', e.target.value)} /></Field>
          <Field label="Short code *"><input style={inp} value={form.short_code || ''} onChange={e => set('short_code', e.target.value.toUpperCase())} placeholder="MEL" /></Field>
          <Field label="Type *">
            <select style={inp} value={form.type || ''} onChange={e => set('type', e.target.value)}>
              <option value="">—</option>
              {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
          <Field label="ABN"><input style={inp} value={form.abn || ''} onChange={e => set('abn', e.target.value)} /></Field>
          <Field label="Contact person"><input style={inp} value={form.contact_person || ''} onChange={e => set('contact_person', e.target.value)} /></Field>
          <Field label="Email"><input style={inp} value={form.email || ''} onChange={e => set('email', e.target.value)} /></Field>
          <Field label="Phone"><input style={inp} value={form.phone || ''} onChange={e => set('phone', e.target.value)} /></Field>
          <Field label="Colour"><input type="color" style={{ ...inp, height: 38, padding: 2 }} value={form.color_hex || '#1e3a4a'} onChange={e => set('color_hex', e.target.value)} /></Field>
          <Field label="Address" full><textarea style={{ ...inp, minHeight: 48 }} value={form.address || ''} onChange={e => set('address', e.target.value)} /></Field>
        </Grid>
      </Section>

      {/* Section 2 — Modules */}
      <Section title="Service modules">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 6 }}>
          {MODULES.map(([key, label]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer', padding: '4px 0' }}>
              <input type="checkbox" checked={!!form[key]} onChange={e => set(key, e.target.checked ? 1 : 0)} />
              {label}
            </label>
          ))}
        </div>
      </Section>

      {/* Section 3 — Zoho + file system */}
      <Section title="Zoho & file system">
        <Grid>
          <Field label="Zoho Customer ID"><input style={inp} value={form.zoho_customer_id || ''} onChange={e => set('zoho_customer_id', e.target.value)} /></Field>
          <Field label="Save folder name" full><input style={inp} value={form.save_folder_name || ''} onChange={e => set('save_folder_name', e.target.value)} placeholder="1 - REPORTS …" /></Field>
        </Grid>
      </Section>

      <div style={{ display: 'flex', gap: 8, margin: '4px 0 18px' }}>
        <button onClick={saveBasics} disabled={saving} style={btnPrimary}>
          {saving ? 'Saving…' : (isNew ? 'Create customer' : 'Save basics')}
        </button>
        {isNew && <span style={{ fontSize: 12, color: MUTED, alignSelf: 'center' }}>
          Create first, then add departments & services.
        </span>}
      </div>

      {/* Departments + Pricing only available once the customer exists */}
      {!isNew && (
        <>
          <DepartmentsEditor
            customerId={id}
            depts={depts}
            onChange={async () => { const c = await api.getCustomer(id); setDepts(c.departments || []); setChanged(true); }}
          />
          <PricingEditor
            customerId={id}
            pricing={pricing}
            allCustomers={allCustomers}
            onChange={async () => { const c = await api.getCustomer(id); setPricing(c.pricing || []); setChanged(true); }}
          />
        </>
      )}
    </Modal>
  );
}

// ── Departments sub-editor ──────────────────────────────────────────────────
function DepartmentsEditor({ customerId, depts, onChange }) {
  const [draft, setDraft] = useState({ name: '', short_code: '', invoice_frequency: 'weekly' });
  const [busy, setBusy]   = useState(false);

  async function add() {
    if (!draft.name.trim() || !draft.short_code.trim()) return;
    setBusy(true);
    try {
      await api.addCustomerDept(customerId, {
        ...draft,
        short_code: draft.short_code.toUpperCase(),
        // Invoice numbers are the code, the year and the week — KIA2641 —
        // since Melville asked for something shorter to read over the phone.
        invoice_prefix: draft.short_code.toUpperCase().slice(0, 3),
        display_order: depts.length + 1,
      });
      setDraft({ name: '', short_code: '', invoice_frequency: 'weekly' });
      await onChange();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }
  async function remove(deptId) {
    if (!window.confirm('Deactivate this department?')) return;
    try { await api.deleteCustomerDept(customerId, deptId); await onChange(); }
    catch (e) { alert(e.message); }
  }

  return (
    <Section title="Departments">
      {depts.length === 0 ? (
        <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>No departments yet.</div>
      ) : (
        <table style={tbl}>
          <thead><tr style={thRow}>
            <th style={th}>Name</th><th style={th}>Code</th><th style={th}>Frequency</th><th style={th}>Prefix</th><th></th>
          </tr></thead>
          <tbody>
            {depts.map(d => (
              <tr key={d.id} style={{ borderTop: `1px solid ${LINE}`, opacity: d.status === 'inactive' ? 0.5 : 1 }}>
                <td style={td}>{d.name}</td>
                <td style={td}>{d.short_code}</td>
                <td style={td}>{d.invoice_frequency || '—'}</td>
                <td style={{ ...td, fontFamily: 'monospace' }}>{d.invoice_prefix || '—'}</td>
                <td style={td}><button onClick={() => remove(d.id)} style={btnDanger}>🗑</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        <input style={{ ...inp, flex: '2 1 160px' }} placeholder="Department name" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />
        <input style={{ ...inp, flex: '1 1 90px' }} placeholder="CODE" value={draft.short_code} onChange={e => setDraft({ ...draft, short_code: e.target.value.toUpperCase() })} />
        <select style={{ ...inp, flex: '1 1 110px' }} value={draft.invoice_frequency} onChange={e => setDraft({ ...draft, invoice_frequency: e.target.value })}>
          <option value="weekly">Weekly</option>
          <option value="daily">Daily</option>
          <option value="on_request">On request</option>
        </select>
        <button onClick={add} disabled={busy} style={btnPrimary}>+ Add</button>
      </div>
    </Section>
  );
}

// ── Pricing / Services sub-editor (the headline feature) ────────────────────
// service_kind controls where the service shows up in the Log Job form:
//   main / extra / car_count (the 3 the boss picks for new services), plus
//   daily / office for the seeded special services.
const KIND_OPTIONS = [
  { value: 'main',      label: 'Main service' },
  { value: 'extra',     label: 'Extra (add-on)' },
  { value: 'car_count', label: 'Per-car' },
  { value: 'daily',     label: 'Daily contract' },
  { value: 'office',    label: 'Office clean' },
];
const KIND_LABEL = Object.fromEntries(KIND_OPTIONS.map(o => [o.value, o.label]));

function PricingEditor({ customerId, pricing, allCustomers, onChange }) {
  const [draft, setDraft] = useState({ service_name: '', charge_ex_gst: '', pay_ex_gst: '', department: '', service_kind: 'main' });
  const [busy, setBusy]   = useState(false);
  const [copyFrom, setCopyFrom] = useState('');

  async function add() {
    if (!draft.service_name.trim()) return alert('Service name required');
    setBusy(true);
    try {
      await api.addCustomerPricing(customerId, {
        service_name:  draft.service_name.trim().toUpperCase(),
        charge_ex_gst: parseFloat(draft.charge_ex_gst) || 0,
        pay_ex_gst:    parseFloat(draft.pay_ex_gst) || 0,
        department:    draft.department.trim() || null,
        service_kind:  draft.service_kind || 'main',
      });
      setDraft({ service_name: '', charge_ex_gst: '', pay_ex_gst: '', department: '', service_kind: 'main' });
      await onChange();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }
  async function saveRow(row) {
    try {
      await api.updateCustomerPricing(customerId, row.id, {
        charge_ex_gst: parseFloat(row.charge_ex_gst) || 0,
        pay_ex_gst:    parseFloat(row.pay_ex_gst) || 0,
        department:    row.department || null,
        service_kind:  row.service_kind || 'main',
        is_active:     row.is_active ? 1 : 0,
      });
      await onChange();
    } catch (e) { alert(e.message); }
  }
  async function remove(priceId) {
    if (!window.confirm('Delete this service?')) return;
    try { await api.deleteCustomerPricing(customerId, priceId); await onChange(); }
    catch (e) { alert(e.message); }
  }
  async function doCopy() {
    if (!copyFrom) return;
    if (!window.confirm('Copy all services/pricing from the selected customer? Existing services are kept.')) return;
    setBusy(true);
    try { const r = await api.copyCustomerPricing(customerId, copyFrom); await onChange(); alert(`Copied ${r.copied} services.`); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }

  return (
    <Section title="Services & pricing">
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>
        Per service: what the customer is charged and what the sub gets paid (both ex GST).
      </div>

      {pricing.length > 0 && (
        <table style={tbl}>
          <thead><tr style={thRow}>
            <th style={th}>Service</th>
            <th style={th}>Charge $</th>
            <th style={th}>Sub pay $</th>
            <th style={th}>Dept</th>
            <th style={th}>Type</th>
            <th style={th}>Active</th>
            <th></th>
          </tr></thead>
          <tbody>
            {pricing.map((p, i) => (
              <PriceRow key={p.id} row={p}
                onSave={saveRow} onRemove={() => remove(p.id)} />
            ))}
          </tbody>
        </table>
      )}

      {/* Add new service */}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...inp, flex: '2 1 150px' }} placeholder="NEW SERVICE NAME" value={draft.service_name}
               onChange={e => setDraft({ ...draft, service_name: e.target.value.toUpperCase() })} />
        <input style={{ ...inp, flex: '1 1 70px' }} type="number" placeholder="Charge" value={draft.charge_ex_gst}
               onChange={e => setDraft({ ...draft, charge_ex_gst: e.target.value })} />
        <input style={{ ...inp, flex: '1 1 70px' }} type="number" placeholder="Sub pay" value={draft.pay_ex_gst}
               onChange={e => setDraft({ ...draft, pay_ex_gst: e.target.value })} />
        <input style={{ ...inp, flex: '1 1 80px' }} placeholder="Dept" value={draft.department}
               onChange={e => setDraft({ ...draft, department: e.target.value })} />
        <select style={{ ...inp, flex: '1 1 110px' }} value={draft.service_kind}
                onChange={e => setDraft({ ...draft, service_kind: e.target.value })}>
          {KIND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <button onClick={add} disabled={busy} style={btnPrimary}>+ Add service</button>
      </div>
      <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>
        <strong>Type</strong> decides where it shows in Log Job — Main = main list · Extra = add-on step · Per-car = quantity is car count.
      </div>

      {/* Copy from another customer */}
      <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: MUTED }}>Copy pricing from:</span>
        <select style={{ ...inp, flex: '0 1 220px' }} value={copyFrom} onChange={e => setCopyFrom(e.target.value)}>
          <option value="">— choose customer —</option>
          {(allCustomers || []).filter(c => String(c.id) !== String(customerId)).map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <button onClick={doCopy} disabled={busy || !copyFrom} style={btnGhost}>Copy</button>
      </div>
    </Section>
  );
}

function PriceRow({ row, onSave, onRemove }) {
  const [r, setR] = useState({ ...row, is_active: row.is_active !== 0, service_kind: row.service_kind || 'main' });
  const dirty = r.charge_ex_gst != row.charge_ex_gst || r.pay_ex_gst != row.pay_ex_gst
             || (r.department || '') !== (row.department || '')
             || (r.service_kind || 'main') !== (row.service_kind || 'main')
             || (!!r.is_active) !== (row.is_active !== 0);
  return (
    <tr style={{ borderTop: `1px solid ${LINE}`, opacity: r.is_active ? 1 : 0.5 }}>
      <td style={td}>{row.service_name}</td>
      <td style={td}><input style={inpSm} type="number" value={r.charge_ex_gst} onChange={e => setR({ ...r, charge_ex_gst: e.target.value })} /></td>
      <td style={td}><input style={inpSm} type="number" value={r.pay_ex_gst} onChange={e => setR({ ...r, pay_ex_gst: e.target.value })} /></td>
      <td style={td}><input style={{ ...inpSm, width: 80 }} value={r.department || ''} onChange={e => setR({ ...r, department: e.target.value })} /></td>
      <td style={td}>
        <select style={{ ...inpSm, width: 96 }} value={r.service_kind} onChange={e => setR({ ...r, service_kind: e.target.value })}>
          {KIND_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>
      <td style={td}><input type="checkbox" checked={!!r.is_active} onChange={e => setR({ ...r, is_active: e.target.checked })} /></td>
      <td style={{ ...td, whiteSpace: 'nowrap' }}>
        {dirty && <button onClick={() => onSave(r)} style={btnPrimarySm}>Save</button>}
        <button onClick={onRemove} style={btnDanger}>🗑</button>
      </td>
    </tr>
  );
}

// ── little building blocks ──────────────────────────────────────────────────
function Modal({ children, onClose, wide }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 600, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: wide ? 760 : 480, padding: 22, marginTop: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        {children}
      </div>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div style={{ border: `1px solid ${LINE}`, borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: NAVY_LT, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>{children}</div>;
}
function Field({ label, children, full }) {
  return (
    <div style={full ? { gridColumn: '1 / -1' } : undefined}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

function blankForm() {
  return {
    name: '', short_code: '', type: '', abn: '', contact_person: '', email: '',
    phone: '', address: '', zoho_customer_id: '', save_folder_name: '',
    color_hex: '#1e3a4a', status: 'active',
    has_daily_invoicing: 0, has_weekly_invoicing: 1, has_office_cleaning: 0,
    has_service_wash: 0, has_detailing: 1, has_buffing: 0,
  };
}

const inp        = { width: '100%', padding: '8px 10px', border: `1px solid #d1d5db`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
const inpSm      = { width: 70, padding: '5px 7px', border: `1px solid #d1d5db`, borderRadius: 5, fontSize: 12.5, boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 16px', borderRadius: 8, border: 'none', background: NAVY, color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' };
const btnPrimarySm = { padding: '4px 10px', borderRadius: 6, border: 'none', background: NAVY, color: '#fff', fontWeight: 700, fontSize: 11, cursor: 'pointer', marginRight: 4 };
const btnGhost   = { padding: '7px 14px', borderRadius: 8, border: `1px solid ${LINE}`, background: '#fff', color: NAVY, fontWeight: 600, fontSize: 12.5, cursor: 'pointer' };
const btnDanger  = { padding: '4px 8px', borderRadius: 5, border: '1px solid #fca5a5', background: '#fff1f1', color: '#b91c1c', fontSize: 11, cursor: 'pointer' };
const errBox     = { background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', padding: '8px 12px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 };
const tbl        = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 };
const thRow      = { background: '#f8fafc', color: '#475569', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 };
const th         = { textAlign: 'left', padding: '6px 8px' };
const td         = { padding: '5px 8px', color: '#0f172a' };
const tagStyle   = (bg, fg) => ({ fontSize: 10, fontWeight: 700, color: fg, background: bg, padding: '1px 6px', borderRadius: 999 });
